import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import themestyle from "../theme";
import { AttendanceReviewModelProps } from "../interfaces/attendanceProps";
import { ATTENDANCE_MESSAGES } from "../utils/const";

const AttendanceReviewModel: React.FC<AttendanceReviewModelProps> = ({
  open,
  onClose,
  onConfirm,
  onReturn,
  totalStudents,
  presentCount,
  walkInCount = 0,
  loading = false,
  isSuccess = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const absentCount = totalStudents - presentCount;

  return (
    <Dialog
      open={open}
      onClose={isSuccess ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "5px",
          p: isMobile ? 0 : 2,
          margin: isMobile ? 1.5 : 4,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: isMobile ? 8 : 16,
          top: isMobile ? 8 : 16,
          color: "#9CA3AF",
          backgroundColor: "#F3F4F6",
          padding: isMobile ? "4px" : "8px",
          "&:hover": {
            backgroundColor: "#E5E7EB",
          },
        }}
        size="small"
      >
        <CloseIcon sx={{ fontSize: isMobile ? 18 : 22 }} />
      </IconButton>
      <DialogContent sx={{ textAlign: "center", py: isMobile ? 3 : 4, px: isMobile ? 2 : 3 }}>
        <Box
          sx={{
            display: "inline-flex",
            p: 1,
            borderRadius: "50%",
            backgroundColor: "#FEE2E2",
            mb: isMobile ? 2 : 3,
          }}
        >
          <CheckCircleIcon
            sx={{
              fontSize: isMobile ? 32 : 40,
              color: "#10B981",
              backgroundColor: "white",
              borderRadius: "50%",
            }}
          />
        </Box>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          fontWeight={700}
          sx={{ mb: isMobile ? 1 : 2, color: "#111827" }}
        >
           {isSuccess
            ? ATTENDANCE_MESSAGES.SUCCESS_TITLE
            : ATTENDANCE_MESSAGES.REVIEW_TITLE}
        </Typography>
        <Typography
          sx={{
            color: "#4B5563",
            lineHeight: 1.5,
            mb: isSuccess ? (isMobile ? 2 : 3) : (isMobile ? 3 : 4),
            fontSize: isMobile ? "13px" : "16px",
          }}
        >
        {isSuccess
            ? ATTENDANCE_MESSAGES.SUCCESS_DESCRIPTION
            : ATTENDANCE_MESSAGES.REVIEW_DESCRIPTION(
                presentCount,
                totalStudents
              )}
        </Typography>
        {isSuccess && (
          <Box
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "12px",
              p: isMobile ? 2 : 3,
              mb: isMobile ? 3 : 4,
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                {presentCount}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: "#9CA3AF", letterSpacing: "0.05em", fontSize: isMobile ? "10px" : "12px" }}
              >
                {ATTENDANCE_MESSAGES.PRESENT}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: isMobile ? 0.5 : 2 }} />
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                {absentCount}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: "#9CA3AF", letterSpacing: "0.05em", fontSize: isMobile ? "10px" : "12px" }}
              >
                 {ATTENDANCE_MESSAGES.ABSENT}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ mx: isMobile ? 0.5 : 2 }} />
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                {walkInCount}
              </Typography>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ color: "#9CA3AF", letterSpacing: "0.05em", fontSize: isMobile ? "10px" : "12px" }}
              >
                {ATTENDANCE_MESSAGES.WALK_IN}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: "flex", gap: isMobile ? 1.5 : 2 }}>
          {isSuccess ? (
            <Button
              fullWidth
              variant="contained"
              onClick={onReturn}
              endIcon={<ArrowForwardIcon />}
              sx={{
                py: isMobile ? 0.8 : 1.5,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                backgroundColor: themestyle.colors.activeGreen,
                fontSize: isMobile ? "14px" : "16px",
                "&:hover": {
                  backgroundColor:themestyle.colors.activeGreen,
                },
              }}
            >
             {ATTENDANCE_MESSAGES.RETURN_TO_CLASSES}
            </Button>
          ) : (
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={onClose}
                sx={{
                  py: isMobile ? 0.8 : 1.5,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#111827",
                  borderColor: "#E5E7EB",
                  fontSize: isMobile ? "14px" : "16px",
                  "&:hover": {
                    borderColor: "#D1D5DB",
                    backgroundColor: "#F9FAFB",
                  },
                }}
              >
                Go Back
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={onConfirm}
                disabled={loading}
                sx={{
                  py: isMobile ? 0.8 : 1.5,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor:themestyle.colors.activeGreen,
                  fontSize: isMobile ? "14px" : "16px",
                  "&:hover": {
                    backgroundColor: themestyle.colors.activeGreen,
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  isMobile ?  ATTENDANCE_MESSAGES.SUBMIT:ATTENDANCE_MESSAGES.SUBMIT_REPORT

                )}
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
export default AttendanceReviewModel;
